// src/components/SearchBar.js
import React, { useState, useEffect, useRef, useCallback } from "react";

const TRENDING = [
  "Nature", "Architecture", "Travel", "Technology",
  "Food", "Fashion", "Abstract", "Wildlife",
  "City", "Ocean", "Portrait", "Minimal",
];

// Related suggestions dictionary
const SUGGESTIONS_MAP = {
  n: ["Nature", "Night sky", "New York", "Neon lights", "Nordic landscape"],
  na: ["Nature", "National park", "Natural light"],
  nat: ["Nature", "Natural light", "National park", "Natural landscape"],
  natu: ["Nature photography", "Natural background", "Nature wallpaper"],
  a: ["Architecture", "Abstract", "Animals", "Autumn", "Aerial view"],
  ar: ["Architecture", "Arctic", "Art", "Ariel view"],
  arc: ["Architecture", "Arctic landscape", "Arch bridge"],
  t: ["Travel", "Technology", "Trees", "Tropical", "Tokyo"],
  tr: ["Travel", "Trees", "Tropical beach", "Train"],
  tra: ["Travel photography", "Transparent water", "Traditional"],
  c: ["City", "Coffee", "Cars", "Coast", "Clouds"],
  ci: ["City lights", "City skyline", "Cinema"],
  f: ["Food", "Fashion", "Forest", "Flowers", "Friends"],
  fo: ["Food photography", "Forest", "Fog", "Football"],
  m: ["Minimal", "Mountains", "Music", "Morning", "Moon"],
  mi: ["Minimal design", "Misty forest", "Mirror"],
  mo: ["Mountains", "Modern architecture", "Morning light", "Moon"],
  p: ["Portrait", "People", "Pink", "Paris", "Purple sunset"],
  po: ["Portrait photography", "Pool", "Positive vibes"],
  b: ["Beach", "Business", "Black and white", "Blue ocean", "Birds"],
  be: ["Beach sunset", "Beautiful nature", "Behind the scenes"],
  s: ["Sunset", "Sky", "Snow", "Street", "Sports"],
  su: ["Sunset", "Summer beach", "Sunflower", "Sunrise"],
  w: ["Wildlife", "Water", "Winter", "Wedding", "Waterfall"],
  wa: ["Waterfall", "Water reflection", "Waves"],
  o: ["Ocean", "Office", "Orange sunset", "Outdoor"],
  oc: ["Ocean waves", "Ocean sunset", "Ocean aerial"],
  l: ["Landscape", "Light", "Love", "Luxury", "Leaves"],
  la: ["Landscape photography", "Lake reflection", "Lavender field"],
};

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SearchBar = ({ onSearch, currentQuery = "" }) => {
  const [value,       setValue]       = useState(currentQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop,    setShowDrop]    = useState(false);
  const [activeIdx,   setActiveIdx]   = useState(-1);
  const [activeChip,  setActiveChip]  = useState("");
  const wrapRef = useRef(null);

  useEffect(() => { setValue(currentQuery); }, [currentQuery]);

  // Generate suggestions based on input
  const getSuggestions = useCallback((q) => {
    if (!q || q.length < 1) return [];
    const lower = q.toLowerCase();

    // Try progressively shorter prefix keys
    for (let len = Math.min(lower.length, 4); len >= 1; len--) {
      const key = lower.slice(0, len);
      if (SUGGESTIONS_MAP[key]) {
        // Filter to only show ones that start with the typed value
        return SUGGESTIONS_MAP[key]
          .filter(s => s.toLowerCase().startsWith(lower))
          .slice(0, 6);
      }
    }

    // Fallback — filter TRENDING list
    return TRENDING
      .filter(s => s.toLowerCase().includes(lower))
      .slice(0, 5);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    setValue(q);
    setActiveIdx(-1);
    if (q.trim().length > 0) {
      const sugg = getSuggestions(q);
      setSuggestions(sugg);
      setShowDrop(sugg.length > 0);
    } else {
      setSuggestions([]);
      setShowDrop(false);
    }
  };

  const doSearch = (q) => {
    const trimmed = (q || value).trim();
    if (!trimmed) return;
    setValue(trimmed);
    setActiveChip(trimmed);
    setShowDrop(false);
    setSuggestions([]);
    onSearch(trimmed);
  };

  const handleSubmit = (e) => { e.preventDefault(); doSearch(); };

  const handleChip = (chip) => {
    setValue(chip);
    setActiveChip(chip);
    setShowDrop(false);
    onSearch(chip);
  };

  const handleClear = () => {
    setValue("");
    setActiveChip("");
    setSuggestions([]);
    setShowDrop(false);
  };

  // Keyboard navigation in dropdown
  const handleKeyDown = (e) => {
    if (!showDrop || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      doSearch(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setShowDrop(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="searchbar" ref={wrapRef}>
      <form className="searchbar__form" onSubmit={handleSubmit}>
        <div className="searchbar__wrap">
          <span className="searchbar__icon"><SearchIcon /></span>
          <input
            type="search"
            className="searchbar__input"
            placeholder="Search millions of free photos…"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowDrop(true)}
            autoComplete="off"
            aria-label="Search photos"
            aria-autocomplete="list"
            aria-expanded={showDrop}
          />
          {value && (
            <button type="button" className="searchbar__clear" onClick={handleClear} aria-label="Clear">
              <XIcon />
            </button>
          )}
          <button type="submit" className="searchbar__submit">Search</button>
        </div>

        {/* Suggestions dropdown */}
        {showDrop && suggestions.length > 0 && (
          <div className="search-dropdown" role="listbox">
            {suggestions.map((s, i) => (
              <button
                key={s}
                role="option"
                aria-selected={i === activeIdx}
                className={`search-dropdown__item${i === activeIdx ? " search-dropdown__item--active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); doSearch(s); }}
              >
                <span className="search-dropdown__icon"><SearchIcon /></span>
                <span className="search-dropdown__text">
                  {/* Bold the matching part */}
                  <span className="search-dropdown__match">{value}</span>
                  {s.slice(value.length)}
                </span>
              </button>
            ))}
          </div>
        )}
      </form>

      {/* Trending chips */}
      <div className="searchbar__chips" role="list" aria-label="Trending searches">
        <span className="searchbar__chips-label">Trending:</span>
        {TRENDING.map((chip) => (
          <button
            key={chip}
            role="listitem"
            className={`chip${activeChip === chip ? " chip--active" : ""}`}
            onClick={() => handleChip(chip)}
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SearchBar;
